/** biome-ignore-all lint/suspicious/noExplicitAny: <Fix Type later> */
import {
	AuthenticationResult,
	Configuration,
	EventMessage,
	EventType,
	InteractionRequiredAuthError,
	PublicClientApplication,
	type RedirectRequest,
} from "@azure/msal-browser";
import { authPersistence } from "../utils/authPersistence";

const msalConfig: Configuration = {
	auth: {
		clientId: import.meta.env.VITE_AZURE_CLIENT_ID || "",
		authority: `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID}`,
		redirectUri: window.location.origin,
		navigateToLoginRequestUrl: false,
		postLogoutRedirectUri: `${window.location.origin}/login`,
	},
	cache: {
		cacheLocation: "localStorage", // This configures where your cache will be stored
		storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
		secureCookies: window.location.protocol === "https:", // Use secure cookies in production
	},
	system: {
		allowNativeBroker: false, // Disables WAM Broker
		loggerOptions: {
			loggerCallback: (
				level: number,
				message: string,
				containsPii: boolean,
			) => {
				if (containsPii) {
					return;
				}
				// Only log errors in production
				if (level === 0) {
					console.error("[MSAL Error]", message);
				}
			},
			piiLoggingEnabled: false,
			logLevel: import.meta.env.DEV ? 2 : 0, // Info in dev, errors only in prod
		},
	} as any,
};

/**
 * Scopes for acquiring tokens to call our backend API.
 * The Application ID URI matches the client ID registered in Azure AD.
 */
export const apiScopes = [
	`api://${import.meta.env.VITE_AZURE_CLIENT_ID}/user_impersonation`,
];

export const msalInstance = new PublicClientApplication(msalConfig);
const MSAL_PROMPT_NONE_REAUTH_KEY = "msal_prompt_none_reauth_attempted";
const MSAL_PROMPT_NONE_REAUTH_TTL_MS = 60_000;
const POST_LOGIN_REDIRECT_KEY = "post_login_redirect";

const AUTH_RESPONSE_QUERY_KEYS = [
	"client_info",
	"code",
	"error",
	"error_description",
	"session_state",
	"state",
];

const RECOVERABLE_MSAL_ERROR_PATTERN =
	/AADSTS50058|AADSTS70008|InteractionRequiredAuthError|consent_required|interaction_required|invalid_grant|login_required|monitor_window_timeout|silent_sso_error/i;

const clearAuthResponseUrl = () => {
	try {
		const url = new URL(window.location.href);
		let didChange = false;

		for (const key of AUTH_RESPONSE_QUERY_KEYS) {
			if (url.searchParams.has(key)) {
				url.searchParams.delete(key);
				didChange = true;
			}
		}

		if (url.hash) {
			url.hash = "";
			didChange = true;
		}

		if (didChange) {
			window.history.replaceState(
				null,
				document.title,
				`${url.pathname}${url.search}${url.hash}`,
			);
		}
	} catch {
		// Ignore URL cleanup failures and continue auth recovery.
	}
};

const getCurrentAppUrl = () =>
	`${window.location.pathname}${window.location.search}${window.location.hash}`;

const getSavedLoginHint = () => authPersistence.getSavedAccount()?.username;

const setPostLoginRedirect = () => {
	sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, getCurrentAppUrl());
};

const getValidPostLoginRedirect = () => {
	const redirectPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
	if (!redirectPath || redirectPath === "/login") {
		return null;
	}

	try {
		const redirectUrl = new URL(redirectPath, window.location.origin);
		if (redirectUrl.origin !== window.location.origin) {
			return null;
		}

		const targetPath = `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
		if (targetPath === "/login" || targetPath === getCurrentAppUrl()) {
			return null;
		}

		return targetPath;
	} catch {
		return null;
	}
};

const restorePostLoginRedirect = (redirectPath: string | null) => {
	if (!redirectPath) {
		return;
	}

	window.history.replaceState(null, document.title, redirectPath);
};

export const clearMsalReauthState = () => {
	sessionStorage.removeItem(MSAL_PROMPT_NONE_REAUTH_KEY);
	sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
};

export const isMsalInteractionRequiredError = (error: unknown): boolean => {
	if (error instanceof InteractionRequiredAuthError) {
		return true;
	}

	if (!(error instanceof Error)) {
		return false;
	}

	const errorText = `${error.name} ${error.message}`;
	return RECOVERABLE_MSAL_ERROR_PATTERN.test(errorText);
};

export const resetMsalSession = async () => {
	await msalInstance.clearCache();
	msalInstance.setActiveAccount(null);
};

export const startLoginRedirect = async ({
	loginHint,
	prompt = "select_account",
	resetPromptNoneAttempt = true,
}: {
	loginHint?: string;
	prompt?: RedirectRequest["prompt"];
	resetPromptNoneAttempt?: boolean;
} = {}) => {
	if (resetPromptNoneAttempt) {
		clearMsalReauthState();
	}

	setPostLoginRedirect();

	await msalInstance.loginRedirect({
		scopes: apiScopes,
		prompt,
		loginHint: loginHint ?? getSavedLoginHint(),
	});
};

export const tryPromptNoneReauth = async (loginHint?: string) => {
	const resolvedLoginHint = loginHint ?? getSavedLoginHint();

	if (!resolvedLoginHint) {
		return false;
	}

	const lastAttempt = Number(
		sessionStorage.getItem(MSAL_PROMPT_NONE_REAUTH_KEY),
	);
	if (
		Number.isFinite(lastAttempt) &&
		Date.now() - lastAttempt < MSAL_PROMPT_NONE_REAUTH_TTL_MS
	) {
		return false;
	}

	sessionStorage.setItem(MSAL_PROMPT_NONE_REAUTH_KEY, String(Date.now()));

	try {
		await startLoginRedirect({
			loginHint: resolvedLoginHint,
			prompt: "none",
			resetPromptNoneAttempt: false,
		});
		return true;
	} catch (error) {
		sessionStorage.removeItem(MSAL_PROMPT_NONE_REAUTH_KEY);
		throw error;
	}
};

export const recoverExpiredMsalSession = async (error: unknown) => {
	if (!isMsalInteractionRequiredError(error)) {
		return false;
	}

	clearAuthResponseUrl();

	try {
		await resetMsalSession();
	} catch (resetError) {
		console.error("[Auth] Failed to reset expired MSAL session");
	}

	return true;
};

// Initialize MSAL instance
const initializeMsal = async () => {
	await msalInstance.initialize();

	// Handle redirect promise (for users returning from redirect flow)
	try {
		const response = await msalInstance.handleRedirectPromise();
		if (response?.account) {
			const postLoginRedirect = getValidPostLoginRedirect();
			// Clear any logout flags if we're successfully logging in
			sessionStorage.removeItem("msal_logout_in_progress");
			clearMsalReauthState();
			msalInstance.setActiveAccount(response.account);
			authPersistence.saveAccount(response.account);
			restorePostLoginRedirect(postLoginRedirect);
		}
	} catch (error) {
		if (!(await recoverExpiredMsalSession(error))) {
			console.error("[Auth] Error handling redirect:", error);
		}
	}

	// Remove the logout flag if it exists (clean up from previous session)
	const justLoggedOut = sessionStorage.getItem("msal_logout_in_progress");
	if (justLoggedOut) {
		sessionStorage.removeItem("msal_logout_in_progress");
	}

	// Add event callback to handle account changes
	msalInstance.addEventCallback((event: EventMessage) => {
		if (event.eventType === EventType.LOGIN_SUCCESS) {
			const authResult = event.payload as AuthenticationResult;
			if (authResult.account) {
				clearMsalReauthState();
				msalInstance.setActiveAccount(authResult.account);
				authPersistence.saveAccount(authResult.account);
			}
		}
	});

	// Check if user is already signed in
	const accounts = msalInstance.getAllAccounts();

	if (accounts.length > 0) {
		// Set the first account as active to enable SSO
		clearMsalReauthState();
		msalInstance.setActiveAccount(accounts[0]);
		authPersistence.saveAccount(accounts[0]);
	} else {
		// No cached accounts, check our persistence
		const savedAccount = authPersistence.getSavedAccount();

		if (savedAccount?.username) {
			// Store the saved username for later use in auth initialization
			// Using a more secure approach with a Symbol to prevent easy access
			const savedUsernameSymbol = Symbol.for("__msal_saved_username");
			(window as any)[savedUsernameSymbol] = savedAccount.username;
		}
	}
};

// Export the initialization promise so it can be awaited where needed
export const msalInitPromise = initializeMsal();
